/**
 * REGISTA AI: Dashboard Page
 * 
 * Simple dashboard to show successful authentication.
 * Displays user info and allows logout.
 */

import { Link } from 'react-router-dom';
import { useApi } from '../lib/api-client';
import { Film, LogOut, User, Users, CheckCircle } from 'lucide-react';

export default function DashboardPage() {
  const { user, logout, isAuthenticated, isLoading } = useApi();

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-dark-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-dark-900 mb-4">Not Authenticated</h2>
          <Link to="/login" className="btn btn-primary">
            Sign In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-dark-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <Film className="w-8 h-8 text-primary-500" />
              <span className="text-xl font-display font-bold text-dark-900">
                Regista AI
              </span>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 px-4 py-2 text-sm text-dark-600 hover:text-dark-900"
            >
              <LogOut className="w-4 h-4" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Success Message */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-6 h-6 text-green-500" />
            <div>
              <h3 className="text-lg font-semibold text-green-800">
                🎉 Authentication Successful!
              </h3>
              <p className="text-green-700">
                D1 authentication system is working correctly.
              </p>
            </div>
          </div>
        </div>

        {/* User Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* User Details */}
          <div className="card">
            <div className="flex items-center space-x-3 mb-4">
              <User className="w-5 h-5 text-primary-500" />
              <h2 className="text-lg font-semibold text-dark-900">User Information</h2>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-dark-600">Email</label>
                <p className="text-dark-900">{user.email}</p>
              </div>
              {user.firstName && (
                <div>
                  <label className="text-sm font-medium text-dark-600">Name</label>
                  <p className="text-dark-900">{user.firstName} {user.lastName}</p>
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-dark-600">User ID</label>
                <p className="text-dark-900 font-mono text-sm">{user.id}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-dark-600">Email Verified</label>
                <p className="text-dark-900">
                  {user.emailVerified ? (
                    <span className="text-green-600">✓ Verified</span>
                  ) : (
                    <span className="text-orange-600">⚠ Not verified</span>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Organization Memberships */}
          <div className="card">
            <div className="flex items-center space-x-3 mb-4">
              <Users className="w-5 h-5 text-primary-500" />
              <h2 className="text-lg font-semibold text-dark-900">Organizations</h2>
            </div>
            {user.memberships && user.memberships.length > 0 ? (
              <div className="space-y-3">
                {user.memberships.map((membership: any, index: number) => (
                  <div key={index} className="border border-dark-200 rounded-lg p-3">
                    <div className="font-medium text-dark-900">{membership.org_name}</div>
                    <div className="text-sm text-dark-600">
                      Role: <span className="font-medium">{membership.role}</span>
                    </div>
                    <div className="text-sm text-dark-600">
                      Squad: {membership.squad_name}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-dark-600">No organization memberships found.</p>
            )}
          </div>
        </div>

        {/* API Test Section */}
        <div className="mt-8">
          <div className="card">
            <h2 className="text-lg font-semibold text-dark-900 mb-4">
              🧪 Authentication Test Results
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-500 mx-auto mb-2" />
                <div className="font-medium text-green-800">JWT Token</div>
                <div className="text-sm text-green-600">Valid & Active</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-500 mx-auto mb-2" />
                <div className="font-medium text-green-800">Session</div>
                <div className="text-sm text-green-600">Authenticated</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-500 mx-auto mb-2" />
                <div className="font-medium text-green-800">API Access</div>
                <div className="text-sm text-green-600">Authorized</div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="mt-8 text-center">
          <Link
            to="/"
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            ← Back to home
          </Link>
        </div>
      </main>
    </div>
  );
}